import { useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminFilterBar } from '../../components/primitives/admin-filter-bar.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminDataTable, type AdminDataTableColumn } from '../../components/primitives/admin-data-table.js';
import { AdminStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { useAdminBrands, useAdminBrandMutations } from '../hooks/use-brands.js';
import { useAdminMediaLibrary } from '../../media/hooks/use-media.js';
import { BrandForm } from '../components/brand-form.js';
import type { AdminBrand } from '../types/catalog.js';

export function BrandsPage() {
  const { data, isLoading, isError, refetch } = useAdminBrands();
  const { create, update, activate, deactivate } = useAdminBrandMutations();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AdminBrand | null>(null);

  const brands = data ?? [];
  const filtered = brands.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()));

  // No dedicated "resolve media asset by id" endpoint exists, so the logo
  // thumbnail reuses the same media-library listing the picker itself
  // queries (kind: image, generously limited) and looks the brand's
  // `logoMediaAssetId` up in it client-side -- consistent with how
  // MediaAssetPicker already sources thumbnails, no new backend surface.
  const { data: mediaLibrary } = useAdminMediaLibrary({ kind: 'image', limit: 200 });
  const logoUrlById = new Map((mediaLibrary ?? []).map((asset) => [asset.id, asset.url]));

  const columns: AdminDataTableColumn<AdminBrand>[] = [
    {
      key: 'logo',
      header: 'Logo',
      render: (row) => {
        const url = row.logoMediaAssetId ? logoUrlById.get(row.logoMediaAssetId) : null;
        return url ? (
          <img src={url} alt="" className="h-8 w-8 rounded object-cover" />
        ) : (
          <div className="h-8 w-8 rounded bg-gray-100" aria-hidden="true" />
        );
      },
    },
    { key: 'name', header: 'Name', render: (row) => row.name },
    { key: 'slug', header: 'Slug', render: (row) => row.slug },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => (
        <AdminStatusBadge label={row.isActive ? 'Active' : 'Inactive'} tone={row.isActive ? 'success' : 'neutral'} />
      ),
    },
  ];

  return (
    <AdminContent>
      <AdminPageHeader
        title="Brands"
        description="Manage the catalog's brand list."
        breadcrumbs={[{ label: 'Brands' }]}
        actions={
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="bg-brand text-brand-foreground inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New brand
          </button>
        }
      />

      <AdminSectionCard title="All brands" className="p-4 sm:p-5">
        {showCreate && (
          <div className="mb-4">
            <BrandForm
              isSubmitting={create.isPending}
              onCancel={() => setShowCreate(false)}
              onSubmit={(input) => create.mutate(input, { onSuccess: () => setShowCreate(false) })}
            />
          </div>
        )}
        {editing && (
          <div className="mb-4">
            <BrandForm
              initial={editing}
              isSubmitting={update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={(input) => update.mutate({ id: editing.id, input }, { onSuccess: () => setEditing(null) })}
            />
          </div>
        )}

        <AdminFilterBar>
          <AdminSearchInput value={search} onChange={setSearch} placeholder="Search brands…" label="Search brands" />
        </AdminFilterBar>

        {isError ? (
          <AdminErrorState message="Brands could not be loaded." onRetry={() => void refetch()} />
        ) : (
          <AdminDataTable
            columns={columns}
            rows={filtered}
            getRowId={(row) => row.id}
            isLoading={isLoading}
            isError={false}
            onRetry={() => void refetch()}
            emptyTitle="No brands yet"
            rowActions={(row) => (
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                >
                  Edit
                </button>
                {row.isActive ? (
                  <button
                    type="button"
                    onClick={() => deactivate.mutate(row.id)}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => activate.mutate(row.id)}
                    className="rounded-full border border-gray-200 px-2.5 py-1 text-xs hover:bg-gray-50"
                  >
                    Activate
                  </button>
                )}
              </div>
            )}
          />
        )}
      </AdminSectionCard>
    </AdminContent>
  );
}
